import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { Heading } from "components/designV2/heading/heading";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import { LocalDate } from "@js-joda/core";
import { useTranslation } from "react-i18next";
import { UnitTeamDropdown } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import type { UnitTeamOption } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import {
  ActionsSlot,
  ControlsRow,
  DateFilterSlot,
  HeaderRow,
  Toolbar,
} from "./timeReportsToolbar.styled";

export type TimeReportsGroupByOption = "person" | "team";

interface TimeReportsToolbarProps {
  unitTeamOptions: UnitTeamOption[];
  selectedWorkGroupIds: string[];
  personOptions: Array<{ value: string; label: string }>;
  unitTeamEmptyText: string;
  personEmptyText: string;
  selectedPersonIds: string[];
  monthDate: LocalDate;
  selectedRangeStart: LocalDate | null;
  selectedRangeEnd: LocalDate | null;
  isAllRowsExpanded: boolean;
  onSelectedWorkGroupIdsChange: (values: string[]) => void;
  onSelectedPersonIdsChange: (values: string[]) => void;
  onDateRangeChange: (dates: LocalDate[]) => void;
  onExpandAllClick: () => void;
  onOptionsClick: () => void;
  showControls?: boolean;
}

export const TimeReportsToolbar: React.FC<TimeReportsToolbarProps> = (
  props,
) => {
  const { t } = useTranslation();
  const shouldShowControls = props.showControls ?? true;

  return (
    <Toolbar>
      <HeaderRow>
        <Heading level="h2">{t("TimeReports.TimeReports")}</Heading>
      </HeaderRow>
      {shouldShowControls && (
        <ControlsRow>
          <UnitTeamDropdown
            label={t("TimeReports.UnitTeam")}
            placeholder={t("TimeReports.AllUnitsTeamsVisible")}
            emptyText={props.unitTeamEmptyText}
            options={props.unitTeamOptions}
            selectedTeamIds={props.selectedWorkGroupIds}
            onSelectedTeamIdsChange={props.onSelectedWorkGroupIdsChange}
          />
          <SearchableDropdown
            label={t("TimeReports.Person")}
            placeholder={t("TimeReports.SearchOrBrowse")}
            emptyText={props.personEmptyText}
            options={props.personOptions}
            leadingIconName="v2/search"
            multiSelect
            selectedValues={props.selectedPersonIds}
            onSelectedValuesChange={props.onSelectedPersonIdsChange}
            onClear={() => props.onSelectedPersonIdsChange([])}
          />
          <DateFilterSlot>
            <DatePicker
              key={`time-reports-date-${props.monthDate.toString()}`}
              label={t("CompanyTimeReport.Date")}
              allowRangePicker
              allowMultiSelectInSingleMode={false}
              allowClear
              value={props.selectedRangeStart}
              onRangeChange={props.onDateRangeChange}
              restrictToMonth={props.monthDate.withDayOfMonth(1)}
              disableNavigationOutsideMonth
            />
          </DateFilterSlot>
          <ActionsSlot>
            <SecondaryButton
              label={
                props.isAllRowsExpanded
                  ? t("TimeReports.CollapseAll")
                  : t("TimeReports.ExpandAll")
              }
              size="small"
              iconName="v2/expand-vertical"
              onClick={props.onExpandAllClick}
            />
            <SecondaryButton
              label={t("TimeReports.Options")}
              size="small"
              iconName="v2/settings"
              onClick={props.onOptionsClick}
            />
          </ActionsSlot>
        </ControlsRow>
      )}
    </Toolbar>
  );
};
